import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { ColumnsPanelTrigger, Toolbar, ToolbarButton } from '@mui/x-data-grid';
import { ClientComponent } from 'components/guardedComponent/ClientComponent';
import { EventOccasion } from 'models/EventOccasion';
import { Track } from 'models/Track';
import { TrackRequest } from 'models/TrackRequest';
import { useTranslation } from 'react-i18next';
import { formatDurationHuman } from 'utils/formatDurationHuman';
import { ExportPlaylistActionMenu } from '../actionMenu/ExportPlaylistActionMenu';

export interface RequestListToolbarProps {
  event: EventOccasion;
  requests: TrackRequest[];
  selectedRequests: TrackRequest[];
  playlistRequests: TrackRequest[];
  loading: boolean;
  onExport?(): void;
  onReload(): void;
  onDelete(requests: TrackRequest[]): void;
  onAddRequestsDialog(): void;
}

export function RequestListToolbar({
  event,
  requests,
  selectedRequests,
  playlistRequests,
  loading,
  onExport,
  onReload,
  onDelete,
  onAddRequestsDialog,
}: RequestListToolbarProps) {
  const { t } = useTranslation();

  return (
    <Toolbar>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flex: 1 }}>
        <Tooltip title={t('loadNewRequests', 'Load new requests')} placement="top">
          <span>
            <ToolbarButton onClick={onReload} disabled={loading}>
              <AutorenewIcon fontSize="small" className={loading ? 'spin' : ''} />
            </ToolbarButton>
          </span>
        </Tooltip>
        <Tooltip title={t('addTrack', 'Add track')} placement="top" sx={{ display: 'none' }}>
          <ToolbarButton onClick={onAddRequestsDialog}>
            <AddIcon fontSize="small" />
          </ToolbarButton>
        </Tooltip>
        <Tooltip title={t('columns', 'Columns')} placement="top">
          <ColumnsPanelTrigger render={<ToolbarButton />}>
            <ViewColumnIcon fontSize="small" />
          </ColumnsPanelTrigger>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <ClientComponent>
              <ToolbarButton
                render={
                  <ExportPlaylistActionMenu
                    playlist={playlistRequests}
                    event={event}
                    onExport={onExport}
                  />
                }
              />
            </ClientComponent>
            {selectedRequests.length > 0 && (
              <ToolbarButton
                render={<Button color="error" variant="outlined" sx={{ lineHeight: 'normal' }} />}
                onClick={() => onDelete?.(selectedRequests)}
              >
                {t('deleteLength', 'Delete ({{length}})', { length: selectedRequests.length })}
              </ToolbarButton>
            )}
          </Stack>
        </Box>
        <Box sx={{ px: 1 }}>
          {selectedRequests.length > 0 ? (
            <Typography>
              {t('lengthSelected', '{{length}} selected', { length: selectedRequests.length })}
              {', '}
              {formatDurationHuman(
                selectedRequests.reduce((sum, request) => {
                  const track = request.value as Track | undefined;
                  return sum + (track?.duration ?? 0);
                }, 0)
              )}
            </Typography>
          ) : (
            <Typography>
              {t('lengthRequests', '{{length}} requests', { length: requests.length })}
              {', '}
              {formatDurationHuman(
                requests.reduce((sum, request) => {
                  const track = request.value as Track | undefined;
                  return sum + (track?.duration ?? 0);
                }, 0)
              )}
            </Typography>
          )}
        </Box>
      </Stack>
    </Toolbar>
  );
}
